import React from 'react'
import { Ellipsis, Row, Card, useContainerWidth, Bleed, Separator, MoreLess } from '@fs/zion-ui'
import { Link } from '@fs/zion-router'
import { css } from '@emotion/core'
import VideoThumbnail from './VideoThumbnail'
import SafeRichText from '../../SafeRichText'
import CreatorExhibitorLinks from '../../CreatorExhibitorLinks'
import { getBrowserLanguage, getCustomImage, getDuration, getPercentageWatched } from '../../helpers'
import { StrongLink } from '../../link'
import { MonoText } from '../../type'
import { useHeadingLevel } from '../../../hooks'

const containerCss = css`
  display: flex;
  align-items: center;
`

const ThumbnailContainer = ({ shouldWrap, to, children, shrinkThumbnail, ...props }) => {
  if (shouldWrap) {
    return (
      <Bleed css={containerCss} top sides>
        <Link to={to} style={{ width: '100%' }} {...props}>
          {children}
        </Link>
      </Bleed>
    )
  }
  const minimumWidth = shrinkThumbnail ? 178 : 286
  return (
    <div css={containerCss} style={{ width: minimumWidth }}>
      <Card to={to} {...props}>
        <Bleed all>{children}</Bleed>
      </Card>
    </div>
  )
}

const BadgesContainer = ({ badges, timestamp, classroom, shouldWrap }) => {
  return shouldWrap ? (
    <div>
      <Row guttersX="none" alignX="end">
        {timestamp}
        {classroom}
      </Row>
      {badges}
    </div>
  ) : (
    <Row>
      <div growX>{badges} </div>
      <Row guttersX="none" noShrinkX>
        {timestamp}
        {classroom}
      </Row>
    </Row>
  )
}

const Description = ({ expandable, description, lineClamp }) => {
  if (!expandable) {
    return (
      <Ellipsis as="span" lineClamp={lineClamp} noTooltip>
        <SafeRichText as="span" dirtyInput={description} />
      </Ellipsis>
    )
  }
  return (
    <MoreLess>
      <SafeRichText as="span" dirtyInput={description} />
    </MoreLess>
  )
}

/**
 * `layout="compact"` changes the narrow card's content order without changing the default card.
 * `subHeader` is a caller-supplied slot directly below the title in that compact layout; it can
 * contain any summary, and falls back to the existing time and classroom slots when omitted.
 */
export default function CardListItem({
  headingLevel = 'h3',
  actions,
  badges,
  classroom,
  creators,
  dataForVideo,
  description,
  exhibitors,
  heading,
  linkName,
  layout,
  subHeader,
  timestamp,
  to,
  expandable,
  additionalContent,
}) {
  const atWidth = useContainerWidth()
  const { comingSoon, contentLocale, linkName: thumbnailLinkName, isSession, shrinkThumbnail } = dataForVideo || {}
  const videoDuration = getDuration(dataForVideo)

  const thumbnail = getCustomImage(dataForVideo)
  const percentageWatched = getPercentageWatched(dataForVideo)
  const hasCreatorsExhibitors = creators?.length > 0 || exhibitors?.length > 0
  const shouldWrap = atWidth({ default: true, lg: false })
  const compactLayout = layout === 'compact' && shouldWrap
  const { Heading } = useHeadingLevel(headingLevel)
  const lang = contentLocale !== getBrowserLanguage() ? contentLocale : undefined
  const title = (
    <Heading lang={lang} size="H5" data-testid="list-item-title">
      <MonoText>
        <StrongLink lang={lang} to={to} noUnderline linkName={linkName}>
          {heading}
        </StrongLink>
      </MonoText>
    </Heading>
  )

  return (
    <Card>
      <Row wrap={shouldWrap} alignX={shouldWrap ? 'center' : undefined}>
        {dataForVideo && (
          <ThumbnailContainer
            shouldWrap={shouldWrap}
            to={to}
            noShrinkX={!shouldWrap}
            growX={shouldWrap}
            linkName={thumbnailLinkName}
            shrinkThumbnail={shrinkThumbnail}
            lang={lang}
          >
            <VideoThumbnail
              lang={lang}
              thumbnail={thumbnail}
              duration={videoDuration}
              heading={heading}
              comingSoon={comingSoon}
              contentLocale={contentLocale}
              percentageWatched={percentageWatched}
              isSession={isSession}
            />
          </ThumbnailContainer>
        )}
        <div growX lang={lang}>
          {compactLayout ? (
            <>
              {badges}
              {badges && <Separator size="xxs" />}
              {title}
              <div data-testid="card-list-item-subheader">
                {subHeader ?? (
                  <Row guttersX="none">
                    {timestamp}
                    {classroom}
                  </Row>
                )}
              </div>
            </>
          ) : (
            <>
              <BadgesContainer badges={badges} timestamp={timestamp} classroom={classroom} shouldWrap={shouldWrap} />
              {(badges || timestamp || classroom) && <Separator size="xxs" />}
              {title}
            </>
          )}
          <CreatorExhibitorLinks creators={creators} exhibitors={exhibitors} />
          <div>
            {additionalContent}
            {(!compactLayout || description) && (
              <Description
                expandable={expandable || compactLayout}
                description={description}
                lineClamp={hasCreatorsExhibitors ? 2 : 3}
              />
            )}
            {(!compactLayout || actions) && <Separator size={compactLayout ? 'xs' : undefined} />}
            {actions}
            {expandable && !compactLayout && <Separator size="nano" />}
          </div>
        </div>
      </Row>
    </Card>
  )
}
